import React from "react";
import PageHeader from "../../../components/layout/PageHeader";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";

/**
 * The four cards of the enquiry screen, at the heights they stand at: who they
 * are, what they asked for, how to reach them, and the payment card.
 *
 * Heights rather than contents. What is inside each card is the card's own
 * business once it lands — drawing the fields twice, once in blocks and once
 * for real, is what makes a loading screen busier than the screen it stands
 * for.
 */
const CARDS = ["h-[131px]", "h-[46px]", "h-[113px]", "h-[74px]"];

export interface EnquiryDetailSkeletonProps {
  /**
   * How long the record has been coming. Only `slow` draws the page; while it
   * is merely `waiting` there is the heading and nothing else, so an enquiry
   * that arrives quickly is never preceded by a screenful of blocks.
   */
  phase: LoadPhase;
}

/**
 * The enquiry screen while the record is being fetched.
 *
 * The page as it will stand — the same header and the same four cards in the
 * same places — so the enquiry lands into a page that is already the right
 * shape. The breadcrumb stays as words: the trail back to the list is true
 * whether or not the enquiry ever arrives.
 */
const EnquiryDetailSkeleton: React.FC<EnquiryDetailSkeletonProps> = ({
  phase,
}) => (
  <div className="h-full flex flex-col overflow-hidden p-page pt-page-top max-md:pb-0">
    <PageHeader
      title={
        <>
          {/* What the heading is read out as, from the moment the request goes
              out. The block beside it stands in for the enquiry's name, which
              nobody knows yet — and which is not drawn at all until the wait is
              worth admitting. */}
          <span className="sr-only">Loading enquiry…</span>
          <SkeletonCard
            phase={phase}
            className="h-5 w-[180px] sm:w-[220px] 3xl:h-6"
          />
        </>
      }
      breadcrumbs={[
        { label: "Enquiries", to: ROUTES.enquiries },
        { label: "Loading…" },
      ]}
    />

    {phase === "slow" && (
      <div className="mt-5 flex flex-col gap-4">
        {CARDS.map((height) => (
          <SkeletonCard key={height} className={height} />
        ))}
      </div>
    )}
  </div>
);

export default EnquiryDetailSkeleton;
